/**
 * My-work slice (PX-W6.3, TASK-EDD04F2F) — the two self-scoped projections:
 * `/my/board`'s engagement rows and the `/my/client` + `/portal` client
 * landing.
 *
 * The my-client 403 (`project.client.landing.side`) is a TYPED refusal, not
 * a failure: it lands in `clientBlocked` with the server's status/code/
 * message and renders as the side-refusal state (AC-6). An empty client
 * portfolio is a 200 empty state, never an error. `callerMember` rows are
 * stored verbatim — the side/access always come from the server projection
 * (INV-9).
 */

import { createAsyncThunk, createSlice } from '@reduxjs/toolkit'

import { isApiError, toApiError } from '@/services'
import {
  fetchFailed,
  fetchStarted,
  fetchSucceeded,
  initialFetchCache,
  invalidate,
  shouldFetch,
  type FetchCache
} from '@/store/fetchCache'
import { crossProjectReadService } from '../services/crossProjectReadService.http'
import type { MyClientLanding, MyWorkRow } from '../types'

export interface MyWorkError {
  /**
   * The SERVER's HTTP status, carried verbatim. Without it a 403 is
   * structurally indistinguishable from a 500 by the time it reaches a page,
   * which is how AC-6.2 shipped unimplemented on two surfaces (merged review
   * C2): they hardcoded a status into `ErrorPanel` and could never branch to
   * `ForbiddenPanel`. `0` means the request never reached the server.
   */
  status: number
  key?: string
  message: string
}

/**
 * The typed my-client refusal — rendered verbatim, never a chooser.
 *
 * ⚠ See `ProjectBlocked.kind`: the union is tagged, not shape-sniffed. Testing
 * `'status' in payload` broke the moment `MyWorkError` gained a status.
 */
export interface ClientBlocked {
  kind: 'blocked'
  status: number
  code?: string
  message: string
}

export interface MyWorkState {
  work: MyWorkRow[]
  workState: FetchCache
  workError: MyWorkError | null
  client: MyClientLanding | null
  clientBlocked: ClientBlocked | null
  clientLoading: boolean
  clientError: MyWorkError | null
}

const initialState: MyWorkState = {
  work: [],
  workState: { ...initialFetchCache },
  workError: null,
  client: null,
  clientBlocked: null,
  clientLoading: false,
  clientError: null
}

const toMyWorkError = (error: unknown): MyWorkError => {
  const apiError = isApiError(error) ? error : toApiError(error)
  return { status: apiError.status, key: apiError.backendKey, message: apiError.message }
}

/** The typed side refusal, or null for an ordinary failure. */
const asClientBlocked = (error: unknown): ClientBlocked | null => {
  if (!isApiError(error)) return null
  if (error.status !== 403 && error.status !== 404) return null
  return { kind: 'blocked', status: error.status, code: error.backendKey, message: error.message }
}

export const fetchMyWork = createAsyncThunk<
  MyWorkRow[],
  string,
  { rejectValue: MyWorkError }
>('myWork/fetch', async (orgId, { rejectWithValue }) => {
  try {
    return await crossProjectReadService.myWork(orgId)
  } catch (error) {
    return rejectWithValue(toMyWorkError(error))
  }
}, {
  condition: (orgId, { getState }) => {
    const { myWork } = getState() as { myWork: MyWorkState }
    return shouldFetch(myWork.workState, orgId)
  }
})

export const fetchMyClient = createAsyncThunk<
  MyClientLanding,
  string,
  { rejectValue: ClientBlocked | MyWorkError }
>('myWork/fetchClient', async (orgId, { rejectWithValue }) => {
  try {
    return await crossProjectReadService.myClientProjects(orgId)
  } catch (error) {
    const blocked = asClientBlocked(error)
    if (blocked !== null) return rejectWithValue(blocked)
    return rejectWithValue(toMyWorkError(error))
  }
})

export const myWorkSlice = createSlice({
  name: 'myWork',
  initialState,
  reducers: {
    myWorkClosed(state) {
      state.work = []
      state.workError = null
      invalidate(state.workState)
    },
    myWorkErrorDismissed(state) {
      state.workError = null
      invalidate(state.workState)
    },
    myClientBlockedDismissed(state) {
      state.clientBlocked = null
      state.clientError = null
      state.clientLoading = false
    }
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchMyWork.pending, (state) => {
        fetchStarted(state.workState)
        state.workError = null
      })
      .addCase(fetchMyWork.fulfilled, (state, action) => {
        state.work = action.payload
        fetchSucceeded(state.workState, action.meta.arg)
      })
      .addCase(fetchMyWork.rejected, (state, action) => {
        fetchFailed(state.workState)
        state.workError = action.payload ?? null
      })
      .addCase(fetchMyClient.pending, (state) => {
        state.clientLoading = true
        state.clientError = null
        state.clientBlocked = null
      })
      .addCase(fetchMyClient.fulfilled, (state, action) => {
        state.clientLoading = false
        state.client = action.payload
        state.clientBlocked = null
      })
      .addCase(fetchMyClient.rejected, (state, action) => {
        state.clientLoading = false
        const payload = action.payload
        if (payload !== undefined && 'kind' in payload && payload.kind === 'blocked') {
          // The typed side refusal — a blocked state, not an error (AC-6).
          state.clientBlocked = payload
          state.client = null
        } else {
          state.clientError = payload ?? null
        }
      })
  }
})

export const { myWorkClosed, myWorkErrorDismissed, myClientBlockedDismissed } =
  myWorkSlice.actions

/* ── Selectors ──────────────────────────────────────────────────────────── */

interface WithMyWork {
  myWork: MyWorkState
}

export const selectMyWorkRows = (s: WithMyWork) => s.myWork.work
export const selectMyWorkLoading = (s: WithMyWork) => s.myWork.workState.loading
export const selectMyWorkError = (s: WithMyWork) => s.myWork.workError
export const selectMyClient = (s: WithMyWork) => s.myWork.client
export const selectMyClientBlocked = (s: WithMyWork) => s.myWork.clientBlocked
export const selectMyClientLoading = (s: WithMyWork) => s.myWork.clientLoading
export const selectMyClientError = (s: WithMyWork) => s.myWork.clientError
